import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["messages", "scrollButton"]

  connect() {
    this.scrollToBottom()
    this.observeNewMessages()
    this.setupScrollListener()
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  observeNewMessages() {
    const messagesContainer = document.getElementById("messages")
    if (!messagesContainer) return

    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Only sort when new .message elements are added, not on content updates during streaming
          const hasNewMessage = Array.from(mutation.addedNodes).some(
            node => node.nodeType === Node.ELEMENT_NODE && node.classList?.contains("message")
          )
          
          if (hasNewMessage) {
            this.sortMessages()
          }
          
          // Only auto-scroll if user is near the bottom
          if (this.isNearBottom()) {
            this.scrollToBottom()
          } else {
            this.showScrollButton()
          }
        }
      }
    })

    this.observer.observe(messagesContainer, {
      childList: true,
      subtree: true,
    })
  }

  sortMessages() {
    const messagesContainer = document.getElementById("messages")
    if (!messagesContainer) return

    const messages = Array.from(messagesContainer.querySelectorAll(".message[data-timestamp]"))
    if (messages.length < 2) return

    const sorted = messages.sort((a, b) => {
      return parseInt(a.dataset.timestamp) - parseInt(b.dataset.timestamp)
    })

    // Only reorder if needed
    let needsReorder = false
    for (let i = 0; i < messages.length; i++) {
      if (messages[i] !== sorted[i]) {
        needsReorder = true
        break
      }
    }

    if (needsReorder) {
      sorted.forEach(msg => messagesContainer.appendChild(msg))
    }
  }

  setupScrollListener() {
    if (!this.hasMessagesTarget) return

    this.messagesTarget.addEventListener("scroll", () => {
      if (this.isNearBottom()) {
        this.hideScrollButton()
      } else {
        this.showScrollButton()
      }
    })
  }

  isNearBottom() {
    if (!this.hasMessagesTarget) return true

    const { scrollTop, scrollHeight, clientHeight } = this.messagesTarget
    const threshold = 100 // pixels from bottom
    return scrollHeight - scrollTop - clientHeight < threshold
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return

    this.messagesTarget.scrollTo({
      top: this.messagesTarget.scrollHeight,
      behavior: "smooth",
    })
    this.hideScrollButton()
  }

  showScrollButton() {
    if (!this.hasScrollButtonTarget) return
    this.scrollButtonTarget.classList.remove("hidden")
  }

  hideScrollButton() {
    if (!this.hasScrollButtonTarget) return
    this.scrollButtonTarget.classList.add("hidden")
  }

  handleKeydown(event) {
    // Submit form on Enter (without Shift)
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      event.target.form.requestSubmit()
    }
  }

  autoResize(event) {
    const textarea = event.target
    textarea.style.height = "auto"
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px"
  }
}
